import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mountain, Map, Brain, Radar, GraduationCap, ArrowRight, Satellite, Droplets } from "lucide-react";

const MARS_HERO = "https://images.unsplash.com/photo-1635349429385-201ee3a51b88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtYXJzJTIwdGVycmFpbiUyMHNhdGVsbGl0ZSUyMHRvcG9ncmFwaHklMjByZWQlMjBwbGFuZXQlMjBzdXJmYWNlfGVufDB8fHx8MTc5MTA1Mjk4OXww&ixlib=rb-4.1.0&q=85";

const features = [
    { icon: Map, title: "Orbital GIS Map", desc: "Explore six Martian quadrants with elevation, slope and subsurface ice layers.", color: "text-ares-orange" },
    { icon: Brain, title: "AI Feature Classifier", desc: "Upload orbital imagery and let AI identify craters, dunes, valleys and lava flows.", color: "text-ares-emerald" },
    { icon: Radar, title: "Subsurface Telemetry", desc: "Visualize water-equivalent-hydrogen depth profiles and thermal cycles.", color: "text-ares-cyan" },
    { icon: GraduationCap, title: "Guided Learning Path", desc: "A 5-stage curriculum from Mars geology to AI feature detection.", color: "text-ares-amber" },
];

export default function Landing() {
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        if (user && user !== false) navigate("/app");
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-ares-void text-ares-bright overflow-x-hidden">
            {/* Nav */}
            <header className="fixed top-0 inset-x-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2.5" data-testid="brand-logo">
                        <div className="w-9 h-9 rounded-lg bg-ares-orange/15 border border-ares-orange/40 flex items-center justify-center glow-orange">
                            <Satellite className="w-5 h-5 text-ares-orange" />
                        </div>
                        <div className="leading-tight">
                            <p className="font-heading font-bold tracking-tight">ARES GEO-ICE</p>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Mars Analysis Workspace</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/login">
                            <Button variant="ghost" className="text-ares-bright hover:text-ares-orange hover:bg-ares-orange/10" data-testid="nav-login-btn">Log in</Button>
                        </Link>
                        <Link to="/register">
                            <Button className="bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="nav-register-btn">Launch Console</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative pt-16 min-h-screen flex items-center">
                <div className="absolute inset-0">
                    <img src={MARS_HERO} alt="Mars surface" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ares-void via-ares-void/80 to-ares-void/30" />
                    <div className="absolute inset-0 radar-grid" />
                </div>
                <div className="relative max-w-7xl mx-auto px-5 py-24 w-full">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ares-orange/90 border border-ares-orange/30 bg-ares-orange/10 rounded-full px-3 py-1 mb-6">
                            <Droplets className="w-3.5 h-3.5" /> Subsurface Ice & Terrain Intelligence
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            Map the Red Planet.<br />
                            <span className="text-ares-orange">Find the water.</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-ares-muted leading-relaxed max-w-xl">
                            A full planetary-science workspace that fuses Mars geology, GIS mapping, subsurface radar telemetry and AI terrain classification — so you can analyze real Martian regions and build a portfolio of actual research.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register">
                                <Button size="lg" className="bg-ares-orange hover:bg-[#e55a2b] text-white h-12 px-7 text-base group" data-testid="hero-get-started-btn">
                                    Start Analyzing <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button size="lg" variant="outline" className="h-12 px-7 text-base border-ares-border bg-transparent hover:bg-ares-panel text-ares-bright" data-testid="hero-login-btn">
                                    I have an account
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section className="relative py-24 border-t border-ares-border">
                <div className="max-w-7xl mx-auto px-5">
                    <div className="max-w-2xl mb-14">
                        <p className="font-mono text-xs uppercase tracking-widest text-ares-cyan mb-3">Mission Modules</p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Everything a Mars analyst needs, in one console</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {features.map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.08 }}
                                className="group relative rounded-xl border border-ares-border bg-ares-dark/90 p-6 hover:-translate-y-1 hover:border-ares-orange/40 transition-all duration-200"
                                data-testid={`feature-card-${i}`}
                            >
                                <div className="w-11 h-11 rounded-lg bg-ares-panel border border-ares-border flex items-center justify-center mb-4">
                                    <f.icon className={`w-5 h-5 ${f.color}`} />
                                </div>
                                <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                                <p className="text-sm text-ares-muted leading-relaxed">{f.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <footer className="border-t border-ares-border py-8">
                <div className="max-w-7xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="font-mono text-xs text-ares-muted">ARES GEO-ICE · Planetary Science Workspace</p>
                    <p className="font-mono text-xs text-ares-muted">Datasets inspired by NASA MRO · MOLA · SHARAD</p>
                </div>
            </footer>
        </div>
    );
}
